import { useLingui } from "@lingui/react/macro";
import { Button } from "@offstreetparking/design-system/forms";
import { WarningIcon } from "@offstreetparking/design-system/icons/WarningIcon";
import { Text } from "@offstreetparking/design-system/typography";
import type { ForwardRefExoticComponent, RefAttributes, SVGProps } from "react";
import { appConfig } from "../../../app.config";
type GeneralErrorAlertProps = {
	icon?: ForwardRefExoticComponent<
		Omit<SVGProps<SVGSVGElement>, "ref"> & {
			title?: string | undefined;
			titleId?: string | undefined;
		} & RefAttributes<SVGSVGElement>
	>;
	title?: string;
	message?: string | string[];
	referenceId?: string;
	showContactSupport?: boolean;
	customAction?: {
		label: string;
		onClick: () => void;
	};
};
export const GeneralErrorAlert = ({
	icon: Icon = WarningIcon,
	title,
	message,
	referenceId,
	showContactSupport = true,
	customAction,
}: GeneralErrorAlertProps) => {
	const { t } = useLingui();
	const displayTitle = title || t`Something went wrong`;
	const displayMessage = message || [
		t`We couldn't load this page. Please try again, and if the issue`,
		t`continues, contact Support.`,
	];
	const messageArray = Array.isArray(displayMessage) ? displayMessage : [displayMessage];
	return (
		<div className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-4">
			<div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
				<div className="rounded-lg bg-white p-2">
					<Icon className="size-10" />
				</div>
				<div className="space-y-2">
					<Text weight="bold" size="2xl">
						{displayTitle}
					</Text>
					<div className="space-y-1">
						{messageArray.map((line) => (
							<Text key={line} intent="muted">
								{line}
							</Text>
						))}
					</div>
					{referenceId && (
						<Text size="sm" intent="muted">
							Reference ID: {referenceId}
						</Text>
					)}
				</div>
				<div className="space-y-4">
					{customAction ? (
						<Button size="lg" intent="primary" className="w-full max-w-xs" onClick={customAction.onClick}>
							{customAction.label}
						</Button>
					) : showContactSupport ? (
						<Button
							size="lg"
							intent="primary"
							className="w-full max-w-xs"
							onClick={() => window.open(appConfig.HELP_CENTER_URL)}
						>
							{t`Contact Support`}
						</Button>
					) : null}
				</div>
			</div>
		</div>
	);
};
